import i18n from "i18n";
import { action, observable } from "mobx";

import type { UsersAPI } from "generated/types";

import { ToastMethods } from "components/ToastNotification";
import { inviteRepository } from "repositories";

interface InviteStoreVariables {
  loading: boolean;
  emailInviteBundleDetails?: UsersAPI.EmailInviteBundleDetails | null;
}

interface InviteStoreSetters {
  setLoading: (isLoading: boolean) => void;
  setEmailInviteBundleDetails: (bundleDetails: UsersAPI.EmailInviteBundleDetails | null) => void;
}

interface InviteApiActions {
  fetchEmailInviteBundleDetails: (emailInviteBundleId: string) => Promise<void>;
  addInvitesToEmailInvitationBundle: (
    emailInviteBundleId: string,
    targetEmails: string[],
  ) => Promise<UsersAPI.AddEmailInviteToBundleResponse>;
}

type InviteStoreManagementProps = InviteStoreVariables & InviteStoreSetters & InviteApiActions;

const initialState: InviteStoreVariables = {
  loading: false,
  emailInviteBundleDetails: null,
};

const stateSetters: InviteStoreSetters = {
  setLoading: action((loading: boolean) => {
    store.loading = loading;
  }),
  setEmailInviteBundleDetails: action((bundleDetails: UsersAPI.EmailInviteBundleDetails | null) => {
    store.emailInviteBundleDetails = bundleDetails;
  }),
};

const apiRequests: InviteApiActions = {
  fetchEmailInviteBundleDetails: action((emailInviteBundleId) => {
    store.setLoading(true);
    return inviteRepository
      .fetchEmailInviteBundleDetails(emailInviteBundleId)
      .then((bundleDetails: UsersAPI.EmailInviteBundleDetails) => {
        store.setEmailInviteBundleDetails(bundleDetails);
      })
      .catch(() => {
        ToastMethods.showToast(
          i18n.t("toast:studentLicense.getEmailInviteBundleDetails.error"),
          "error",
        );
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
  // Add additional invitations to an existing email invite bundle
  addInvitesToEmailInvitationBundle: action((emailInviteBundleId, targetEmails = []) => {
    store.setLoading(true);
    return inviteRepository
      .addInviteToEmailInvitationBundle(emailInviteBundleId, targetEmails)
      .then((result: UsersAPI.AddEmailInviteToBundleResponse) => {
        const { success: addedEmails, failed: notAddedEmails } = result;

        if (addedEmails.length > 0) {
          ToastMethods.showToast(
            i18n.t("toast:studentLicense.addInvitesToEmailInvitationBundle.success", {
              targetEmails: addedEmails.join(", "),
            }),
            "success",
          );
        }

        if (notAddedEmails.length > 0) {
          ToastMethods.showToast(
            i18n.t("toast:studentLicense.addInvitesToEmailInvitationBundle.error", {
              targetEmails: notAddedEmails.join(", "),
            }),
            "error",
          );
        }

        return result;
      })
      .catch(() => {
        ToastMethods.showToast(
          i18n.t("toast:studentLicense.addInvitesToEmailInvitationBundle.genericError"),
          "error",
        );

        return {
          success: [],
          failed: targetEmails,
        };
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
};

const store = observable({
  ...initialState,
  ...stateSetters,
  ...apiRequests,
} as InviteStoreManagementProps);

export const useInviteManagement = (): InviteStoreManagementProps => store;
